"use client";

import { ISeoContent } from "@/shared/models/content/seo-content";
import NotFoundSeoUpdater from "@/components/NotFoundSeoUpdater";
import ErrorBoundaryTracker from "@/components/ErrorBoundaryTracker";
import Link from "next/link";
import { useLayoutConfigProvider } from "@/lib/context/LayoutConfigProvider";
import { SiteHeaderTypes } from "@/shared/enums/site-header-types.enum";
import { useEffect } from "react";
import { trackErrorEvent } from "@/lib/utils/ga4-events";
import { isEventTrackingEnabled } from "@/lib/utils/ga4-utils";
import styles from "@/components/status-page/status-page.module.scss";

const notFoundSeo: ISeoContent = {
  meta_title: "Page not found",
  meta_description: "This page doesn't exist. The link may be old or mistyped.",
  meta_robots: "noindex"
};

export default function NotFound() {
  const { setHeaderType } = useLayoutConfigProvider();
  // Check if tracking is enabled after hooks are called
  const trackingEnabled = isEventTrackingEnabled();

  // Force BLUR header style for 404 page regardless of URL pattern
  useEffect(() => {
    setHeaderType(SiteHeaderTypes.BLUR);
  }, [setHeaderType]);

  useEffect(() => {
    if (!trackingEnabled) {
      return;
    }

    const timeoutId = setTimeout(() => {
      trackErrorEvent(new Error("404"));
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [trackingEnabled]);

  // Design-system 404 (sweep S-C4; no Figma frame): the old slate screen told a visitor who
  // mistyped a URL to log in. Now: what happened, home, sessions, support.
  return (
    <ErrorBoundaryTracker pageName="404">
      <main className={styles.page}>
        <div className={styles.card}>
          <p className={styles.code}>404</p>
          <h1 className={styles.title}>This page doesn&apos;t exist</h1>
          <p className={styles.text}>The link may be old or mistyped. You can head back home or find a session.</p>
          <div className={styles.actions}>
            <Link className={styles.primary} href="/">Go to homepage</Link>
            <Link className={styles.secondary} href="/sessions">Find a session</Link>
          </div>
          <p className={styles.help}>
            Still stuck? <Link href="/contact">Contact support</Link>
          </p>
        </div>
      </main>

      {/* Update SEO context on client side */}
      <NotFoundSeoUpdater notFoundSeo={notFoundSeo} />
    </ErrorBoundaryTracker>
  );
}
